// Is the platform unreachable, or is the person signed out? (Board #2649.)
//
// While the api restarts (a rollout, a crashed pod) the edge answers 502/503/504 or the connection is
// refused. That says nothing about the session: only an auth answer (401) does. The web app shows
// "Reconnecting…" and retries with a backoff instead of sending the person to the sign-in page.

/** The statuses the edge (nginx, the load balancer) answers while the service behind it is down. */
export const OUTAGE_STATUSES: ReadonlySet<number> = new Set([502, 503, 504]);

/** What an answer of the session check (GET /api/me, the accounts refresh) means. */
export type SessionVerdict = 'signed-in' | 'signed-out' | 'unavailable';

/**
 * The verdict of one answer: a 2xx is a session, a 401 the end of one, a network failure (status null), a
 * 5xx, 408 or 429 a platform that cannot answer now. Any other 4xx (403: the account is refused) is
 * signed out, with the status shown on the sign-in page.
 */
export function sessionVerdict(status: number | null): SessionVerdict {
  if (status === null) return 'unavailable';
  if (status >= 200 && status < 300) return 'signed-in';
  if (status >= 500 || status === 408 || status === 429) return 'unavailable';
  return 'signed-out';
}

/** The wait before retry `attempt` (0-based): 1 s, 2 s, 4 s, 8 s, then every 10 s. */
export function reconnectDelay(attempt: number): number {
  return Math.min(1000 * 2 ** Math.max(0, attempt), 10_000);
}

/** True for an aborted fetch (a page left, a query cancelled): that is not an outage. */
export const isAbort = (e: unknown) =>
  typeof e === 'object' && e !== null && 'name' in e && (e as { name: unknown }).name === 'AbortError';

type Listener = () => void;
const listeners = new Set<Listener>();

/** Called when a request of the web app found the api down (an outage status, or no answer at all). */
export function reportOutage() {
  for (const l of listeners) l();
}

/** Subscribes to outage reports; returns the unsubscribe. */
export function onOutage(l: Listener): () => void {
  listeners.add(l);
  return () => listeners.delete(l);
}

/**
 * A fetch that reports an outage: an answer with an outage status, or a request that failed without an
 * answer (not an abort). The answer or the error goes back to the caller unchanged. The read cache sends
 * every request of the page to the network through it (read-cache-install.ts), so every API client (the
 * app's, each SDK host's) and its background reads are watched.
 */
export function watchFetch(f: typeof fetch): typeof fetch {
  return async (input, init) => {
    let res: Response;
    try {
      res = await f(input, init);
    } catch (e) {
      if (!isAbort(e)) reportOutage();
      throw e;
    }
    if (OUTAGE_STATUSES.has(res.status)) reportOutage();
    return res;
  };
}

/** watchFetch over the global fetch, read on every call (so a test may replace it). */
export const watchedFetch: typeof fetch = watchFetch((input, init) => globalThis.fetch(input, init));
